import { IOption } from '@skf/bt-wind-rail-commonlibrary/dist/components/form/types';
import {
  BasicIndustryRole,
  Company,
  Country,
  DistributorApiDTO,
  IIndustryRoleLabelInfo,
  RemanXpDistributor,
  RolesInfo,
  SelectedRoleInfo,
} from '../../../../../store/roles/types';
import { TaxonomyIndustry } from '../../../../../store/taxonomy/types';
import { UserRoleInformative } from '../../../../../store/users/types';

/**
 * Filters
 */
export const mapFilteredCountries = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): Country[] => {
  if (selectedRoleInfo?.companyId && selectedRoleInfo.siteCountryCode) {
    return mapAllCountries(rolesInfo, selectedRoleInfo).filter(
      (country) =>
        country.companies.filter(
          (company) => company.companyId === selectedRoleInfo.companyId
        ).length > 0
    );
  } else if (selectedRoleInfo?.companyId && !selectedRoleInfo.siteCountryCode) {
    return (
      rolesInfo
        .find(
          (country) =>
            !selectedRoleInfo ||
            selectedRoleInfo.industryCode === country.industryCode
        )
        ?.companies.find(
          (company) => company.companyId === selectedRoleInfo.companyId
        )
        ?.country.map(
          (country): Country => {
            return {
              companies: mapAllCompanies(rolesInfo, selectedRoleInfo),
              siteCountryCode: country.siteCountryCode,
              siteCountryDescription: country.siteCountryDescription,
            };
          }
        ) || []
    );
  } else {
    return mapAllCountries(rolesInfo, selectedRoleInfo);
  }
};

export const mapAllCountries = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): Country[] => {
  return rolesInfo
    .filter(
      (country) =>
        !selectedRoleInfo ||
        selectedRoleInfo.industryCode === country.industryCode
    )
    .map((industry) => industry.countries)
    .flat(1);
};

export const mapAllCompanies = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): Company[] => {
  const noDuplicatesCompany: Company[] = [];
  const companiesFiltered = mapAllCountries(rolesInfo, selectedRoleInfo)
    .map((country) => country.companies)
    .flat(1);
  companiesFiltered.forEach((company) => {
    if (!noDuplicatesCompany.find((x) => x.companyId === company.companyId)) {
      noDuplicatesCompany.push(company);
    }
  });
  return noDuplicatesCompany;
};

export const mapFilteredTotalCountries = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): Country[] => {
  return rolesInfo
    .filter(
      (country) =>
        !selectedRoleInfo ||
        selectedRoleInfo.industryCode === country.industryCode
    )
    .map((industry) => industry.totalCountries)
    .flat(1);
};

export const mapFilteredCompanies = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): Company[] => {
  if (selectedRoleInfo?.siteCountryCode && !selectedRoleInfo.companyId) {
    return mapFilteredCountries(rolesInfo, selectedRoleInfo)
      .filter(
        (country) =>
          !selectedRoleInfo ||
          selectedRoleInfo.siteCountryCode === country.siteCountryCode
      )
      .map((country) => country.companies)
      .flat(1);
  } else if (selectedRoleInfo?.siteCountryCode && selectedRoleInfo.companyId) {
    const noDuplicatesCompany: Company[] = [];
    const companiesFiltered = mapFilteredCountries(rolesInfo, selectedRoleInfo)
      .filter(
        (country) =>
          country.siteCountryCode === selectedRoleInfo.siteCountryCode
      )
      .map((country) => country.companies)
      .flat(1);
    companiesFiltered.forEach((company) => {
      if (!noDuplicatesCompany.find((x) => x.companyId === company.companyId)) {
        noDuplicatesCompany.push(company);
      }
    });
    return noDuplicatesCompany;
  } else {
    const noDuplicatesCompany: Company[] = [];
    const companiesFiltered = mapFilteredCountries(rolesInfo, selectedRoleInfo)
      .map((country) => country.companies)
      .flat(1);
    companiesFiltered.forEach((company) => {
      if (!noDuplicatesCompany.find((x) => x.companyId === company.companyId)) {
        noDuplicatesCompany.push(company);
      }
    });
    return noDuplicatesCompany;
  }
};

export const mapFilteredPlantUnits = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
) =>
  mapFilteredCompanies(rolesInfo, selectedRoleInfo)
    .filter(
      (country) =>
        !selectedRoleInfo || selectedRoleInfo.companyId === country.companyId
    )
    .map((country) => country.plantUnits)
    .flat(1);

/**
 * Group By
 */

export const groupByIndustries = (
  selectedRoleInfo: SelectedRoleInfo[]
): BasicIndustryRole[] => {
  const industryRoles: BasicIndustryRole[] = [];

  selectedRoleInfo.forEach((roleInfo) => {
    if (
      industryRoles.findIndex(
        (role) => role.industryCode === roleInfo.industryCode
      ) === -1
    )
      industryRoles.push({
        industryCode: roleInfo.industryCode!,
        industryOrder: roleInfo.industryOrder || 0,
        industryText: roleInfo.industryText!,
      });
  });

  return industryRoles;
};

/**
 * maps to IOptions
 */

export const mapIOptionsIndustries = (rolesInfo: RolesInfo = []): IOption[] =>
  rolesInfo.map((industry) => ({
    value: industry.industryCode,
    labelToShow: industry.industryName,
    label: industry.industryName,
  }));

export const mapIOptionsCountries = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): IOption[] =>
  mapFilteredCountries(rolesInfo, selectedRoleInfo).map((country) => ({
    value: country.siteCountryCode,
    labelToShow: country.siteCountryDescription,
    label: country.siteCountryDescription,
  }));

export const mapIOptionsTotalCountries = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): IOption[] =>
  mapFilteredTotalCountries(rolesInfo, selectedRoleInfo).map((country) => ({
    value: country.siteCountryCode,
    labelToShow: country.siteCountryDescription,
    label: country.siteCountryDescription,
  }));

export const mapIOptionsCompanies = (
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): IOption[] =>
  mapFilteredCompanies(rolesInfo, selectedRoleInfo).map((company) => ({
    value: company.companyId,
    labelToShow: company.companyName,
    label: company.companyName,
  }));

export const PreLabelTaxonomyIndustryMap = {
  [TaxonomyIndustry.RAIL]: 'Site',
  [TaxonomyIndustry.WIND]: 'Plant',
};

export const mapIOptionsPlantUnits = (
  t: (m: string) => string,
  rolesInfo: RolesInfo = [],
  selectedRoleInfo?: SelectedRoleInfo
): IOption[] => {
  const preLabel = t(
    PreLabelTaxonomyIndustryMap[selectedRoleInfo?.industryCode || 0]
  );
  return mapFilteredPlantUnits(rolesInfo, selectedRoleInfo).map(
    (plantUnit) => ({
      value: plantUnit.plantUnitId,
      labelToShow: plantUnit.plantUnitDescription,
      label: `${preLabel} (${plantUnit.plantUnitDescription})`,
    })
  );
};

const PlaceHolderTaxonomyIndustryMap = {
  [TaxonomyIndustry.RAIL]: 'Site_(Fleet)',
  [TaxonomyIndustry.WIND]: 'Plant_(WindFarm)',
  [-1]: '-',
};

export const getPlaceHolderTaxonomyIndustry = (
  t: (m: string) => string,
  industryCode?: number
): string =>
  industryCode !== undefined
    ? t(PlaceHolderTaxonomyIndustryMap[industryCode])
    : '-';

export const mapUserRoleInformativeToSelectedRoleInfosNoDuplicates = (
  roles: UserRoleInformative[]
): SelectedRoleInfo[] => {
  const mapped: SelectedRoleInfo[] = [];
  // console.log('MAPPING SELECTED ROLES roles', roles);
  // console.log(
  //   'MAPPING SELECTED ROLES roles filtered',
  //   roles.filter((x) => x.plantUnit && x.industryCode !== 2)
  // );
  roles
    .filter((x) => x.plantUnit && x.industryCode !== 2)
    .forEach((x) => {
      const mappedObj: SelectedRoleInfo = {
        industryCode: x.industryCode,
        industryName: x.industryName,
        industryText: x.industryText,
        siteCountryCode: x.siteCountryCode,
        siteCountryDescription: x.siteCountryDescription,
        companyId: x.companyId,
        companyName: x.companyName,
        plantUnitId: x.plantUnit,
        plantUnitDescription: x.plantUnit,
      };

      // console.log('MAPPING SELECTED ROLES mappedObj', mappedObj);
      const alreadyAdded =
        mapped.findIndex(
          (y) =>
            y.industryCode === mappedObj.industryCode &&
            y.siteCountryCode === mappedObj.siteCountryCode &&
            y.companyId === mappedObj.companyId &&
            y.plantUnitId === mappedObj.plantUnitId
        ) !== -1;
      if (!alreadyAdded) {
        mapped.push(mappedObj);
      }
    });
  return mapped;
};

export const mapUserRoleInformativeToSelectedRoles = (
  roles: UserRoleInformative[]
): Record<string, string[]> => {
  const roleRecord: Record<string, string[]> = {};
  const uniqueIndustries = [...new Set(roles.map((x) => x.industryText))];
  uniqueIndustries.forEach((industryName) => {
    const uniquePortalsByIndustry = [
      ...new Set(
        roles
          .filter((y) => y.industryText === industryName)
          .flatMap((z) => z.portalCode)
      ),
    ];
    uniquePortalsByIndustry.forEach((portalNameIndustry) => {
      roleRecord[`${industryName}_${portalNameIndustry}`] = roles
        .filter(
          (y) =>
            y.industryText === industryName &&
            y.portalName === portalNameIndustry
        )
        .map((y) => y.roleId);
    });
  });

  return roleRecord;
};

export const mapUserRoleInformativeToIndustryRoleLabelInfo = (
  roles: UserRoleInformative[],
  rolesInfo: RolesInfo | undefined
): IIndustryRoleLabelInfo[] => {
  const mappedTags: IIndustryRoleLabelInfo[] = [];
  const uniqueIndustries = [...new Set(roles.map((x) => x.industryCode))];
  uniqueIndustries.forEach((industryCode) => {
    const uniquePortalsByIndustry = [
      ...new Set(
        roles
          .filter((y) => y.industryCode === industryCode)
          .flatMap((z) => z.portalCode)
      ),
    ];
    uniquePortalsByIndustry.forEach((portalNameIndustry) => {
      const rolesInfoIndustryPortal =
        rolesInfo
          ?.find(
            (roleInfo) =>
              roleInfo.industryName ===
              roles.find((role) => role.industryCode === industryCode)
                ?.industryText
          )
          ?.industryRoles.find(
            (industryRole) => industryRole.portal === portalNameIndustry
          )?.industryText || portalNameIndustry;
      mappedTags.push({
        industryCode: industryCode,
        industryPortal: rolesInfoIndustryPortal,
        values: [
          ...new Set(
            roles
              .filter(
                (role) =>
                  role.industryCode === industryCode &&
                  role.portalCode === portalNameIndustry
              )
              .map((filterRole) => filterRole.roleId)
          ),
        ],
      });
    });
  });

  return mappedTags;
};

export const mapDistributorDataToIOption = (
  data: DistributorApiDTO[]
): IOption[] => {
  return data.map<IOption>((x) => {
    return {
      label: `${x.distributorAccount} - ${x.distributorName} - ${x.officeName}`,
      labelToShow: x.distributorName,
      value: x.distributorAccount,
    };
  });
};
